// src/operator/console/tenant-object-workspace.tsx
//
// Workspace de paramétrage d'un client ou d'un site, monté dans un onglet
// « tenant-object » du cockpit (pas de session relay). Quatre sous-sections :
//   - obj:infos   : métadonnées + résumé + actions (renommer/supprimer)
//   - obj:rbac    : droits (grants) + stratégies (policy) + partages
//   - obj:volt    : credential vault filtré par client/site
//   - obj:hosts   : hôtes rattachés + raccourci connexion
//
// La sous-section active est pilotée par le signal `section` global du
// cockpit (cf. app.tsx) ; ce composant reçoit `activeSection` en prop.

import { createSignal, createResource, Show, For, type Component } from "solid-js";
import { useConfirm } from "../shared/confirm-dialog";
import { useNotify } from "../shared/notify";
import type { HostData } from "../app";
import {
  fetchClients,
  fetchSitesForClient,
  fetchGrants,
  patchClient,
  patchSite,
  deleteClient,
  deleteSite,
  type Client,
  type Site,
  type Grant,
} from "./tenant-api";
import { TenantObjectRbac } from "./tenant-object-rbac";
import { TenantObjectVault } from "./tenant-object-vault";
import "./tenant-object-workspace.css";

export interface TenantObjectRef {
  objectType: "client" | "site";
  objectId: string;
  objectLabel: string;
  parentLabel?: string;
}

interface Props {
  ref: TenantObjectRef;
  activeSection: string;
  hosts: HostData[];
  onConnectHost: (h: HostData) => void;
}

const CLIENT_SECTION_IDS = new Set(["obj:infos", "obj:rbac", "obj:volt", "obj:hosts"]);

export const TenantObjectWorkspace: Component<Props> = (props) => {
  const confirmDialog = useConfirm();
  const notify = useNotify();
  const [renameValue, setRenameValue] = createSignal("");
  const [saving, setSaving] = createSignal(false);

  // Charge le client ou le site selon objectType.
  const [record, { refetch: refetchRecord }] = createResource<
    { kind: "client"; client: Client } | { kind: "site"; site: Site; client: Client | null }
  >(async () => {
    if (props.ref.objectType === "client") {
      const clients = await fetchClients();
      const client = clients.find((c) => c.id === props.ref.objectId) ?? null;
      if (client) return { kind: "client", client };
      // Fallback : objet non trouvé (peut-être hors périmètre) — on fabrique un
      // client fantôme pour que l'UI reste utilisable.
      return {
        kind: "client",
        client: {
          id: props.ref.objectId,
          ownerTenantId: "",
          name: props.ref.objectLabel,
          slug: "",
          createdAt: "",
          policy: null,
        },
      };
    }
    // site : on charge les sites via l'API et on retrouve le parent.
    const sites = (await (await fetch("/api/sites", { credentials: "include" }).then((r) => r.json())).sites) ?? [];
    const site: Site | undefined = (sites as Site[]).find((s) => s.id === props.ref.objectId);
    const clients = await fetchClients();
    const parent = site ? (clients.find((c) => c.id === site.clientId) ?? null) : null;
    const resolvedSite: Site = site ?? {
      id: props.ref.objectId,
      clientId: parent?.id ?? "",
      name: props.ref.objectLabel,
      createdAt: "",
      policy: null,
    };
    return { kind: "site", site: resolvedSite, client: parent };
  });

  // Initialise le champ de renommage quand le record arrive.
  let renameInitialized = false;
  createResource(record, () => {
    const r = record();
    if (r && !renameInitialized) {
      renameInitialized = true;
      setRenameValue(r.kind === "client" ? r.client.name : r.site.name);
    }
    return null;
  });

  // Grants pour le résumé (comptage sur le scope courant).
  const [allGrants] = createResource<Grant[]>(() => fetchGrants());
  const grantsForScope = () => {
    const grants = allGrants() ?? [];
    return grants.filter((g) => g.scopeType === props.ref.objectType && g.scopeId === props.ref.objectId);
  };

  const clientSites = createResource(async (): Promise<Site[]> => {
    const r = record();
    if (!r || r.kind !== "client") return [];
    try {
      return await fetchSitesForClient(r.client.id);
    } catch {
      return [];
    }
  })[0];

  async function saveRename() {
    const r = record();
    if (!r || saving()) return;
    const name = renameValue().trim();
    if (!name) {
      notify.warn("Le nom ne peut pas être vide");
      return;
    }
    setSaving(true);
    try {
      if (r.kind === "client") {
        await patchClient(r.client.id, { name });
      } else {
        await patchSite(r.site.id, { name });
      }
      await refetchRecord();
      notify.ok("Renommé", { hint: name, durationMs: 2500 });
    } catch (err) {
      notify.danger("Échec du renommage", { hint: err instanceof Error ? err.message : String(err) });
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    const r = record();
    if (!r) return;
    const label = r.kind === "client" ? r.client.name : r.site.name;
    const ok = await confirmDialog.confirm({
      title: `Supprimer ${props.ref.objectType === "client" ? "le client" : "le site"} « ${label} » ?`,
      body:
        props.ref.objectType === "client"
          ? "Tous les sites de ce client seront aussi supprimés. Les hôtes ne seront pas supprimés mais perdront leur rattachement. Cette action est irréversible."
          : "Les hôtes rattachés ne seront pas supprimés mais perdront leur rattachement. Cette action est irréversible.",
      confirmLabel: "Supprimer",
      level: "danger",
    });
    if (!ok) return;
    try {
      if (r.kind === "client") await deleteClient(r.client.id);
      else await deleteSite(r.site.id);
      notify.ok("Supprimé", { hint: label, durationMs: 2500 });
      // Ferme l'onglet en simulant un changement ; le parent gérera le retrait.
      window.dispatchEvent(new CustomEvent("rws:tenant-object:close", { detail: { objectId: props.ref.objectId } }));
    } catch (err) {
      notify.danger("Échec de la suppression", { hint: err instanceof Error ? err.message : String(err) });
    }
  }

  const title = () => props.ref.objectLabel;
  const parentName = () => {
    if (props.ref.objectType !== "site") return "";
    if (props.ref.parentLabel) return props.ref.parentLabel;
    const r = record();
    return r && r.kind === "site" ? (r.client?.name ?? "") : "";
  };
  const subtitle = () => (props.ref.objectType === "client" ? "Client (MSP / société)" : `Site · ${parentName()}`);

  return (
    <div class="tenant-workspace">
      <header class="tenant-workspace__header">
        <div class="tenant-workspace__title">
          <span class="tenant-workspace__icon">{props.ref.objectType === "client" ? "🏢" : "📍"}</span>
          <div>
            <h2>{title()}</h2>
            <small>{subtitle()}</small>
          </div>
        </div>
      </header>

      <Show when={!CLIENT_SECTION_IDS.has(props.activeSection)}>
        <div class="tenant-workspace__fallback">
          <Show when={record()} fallback={<div class="tenant-loading">Chargement…</div>}>
            <p>Sélectionnez un sous-onglet : Infos, Droits &amp; Stratégies, VOLT ou Hôtes.</p>
          </Show>
        </div>
      </Show>

      {/* ── Sous-section : Infos ── */}
      <Show when={props.activeSection === "obj:infos"}>
        <Show when={record()} fallback={<div class="tenant-loading">Chargement…</div>}>
          {(rec) => (
            <section class="tenant-section">
              <div class="tenant-grid">
                <div class="tenant-field">
                  <label>Nom</label>
                  <div class="tenant-rename">
                    <input value={renameValue()} onInput={(e) => setRenameValue(e.currentTarget.value)} />
                    <button class="btn btn--primary btn--sm" onClick={saveRename} disabled={saving()}>
                      {saving() ? "…" : "Renommer"}
                    </button>
                  </div>
                </div>
                <Show when={rec().kind === "client"}>
                  <div class="tenant-field">
                    <label>Slug</label>
                    <code>{(rec() as { kind: "client"; client: Client }).client.slug || "—"}</code>
                  </div>
                </Show>
                <div class="tenant-field">
                  <label>Identifiant</label>
                  <code class="tenant-id">{props.ref.objectId}</code>
                </div>
                <Show when={rec().kind === "client"}>
                  <div class="tenant-field">
                    <label>Tenant propriétaire</label>
                    <code>{(rec() as { kind: "client"; client: Client }).client.ownerTenantId || "—"}</code>
                  </div>
                </Show>
                <Show when={rec().kind === "site" && (rec() as { kind: "site"; client: Client | null }).client}>
                  <div class="tenant-field">
                    <label>Client parent</label>
                    <span>{(rec() as { kind: "site"; client: Client | null }).client!.name}</span>
                  </div>
                </Show>
              </div>

              <div class="tenant-stats">
                <div class="tenant-stat">
                  <span class="tenant-stat__num">{props.hosts.length}</span>
                  <span class="tenant-stat__lbl">hôtes</span>
                </div>
                <div class="tenant-stat">
                  <span class="tenant-stat__num">{grantsForScope().length}</span>
                  <span class="tenant-stat__lbl">grants RBAC</span>
                </div>
                <Show when={rec().kind === "client"}>
                  <div class="tenant-stat">
                    <span class="tenant-stat__num">{(clientSites() ?? []).length}</span>
                    <span class="tenant-stat__lbl">sites</span>
                  </div>
                </Show>
              </div>

              <div class="tenant-actions">
                <button class="btn btn--danger btn--sm" onClick={remove}>
                  Supprimer {props.ref.objectType === "client" ? "le client" : "le site"}
                </button>
              </div>
            </section>
          )}
        </Show>
      </Show>

      {/* ── Sous-section : Droits & Stratégies ── */}
      <Show when={props.activeSection === "obj:rbac"}>
        <TenantObjectRbac
          objectType={props.ref.objectType}
          objectId={props.ref.objectId}
          objectLabel={props.ref.objectLabel}
          policy={(() => {
            const r = record();
            if (!r) return null;
            if (r.kind === "client") return r.client.policy ?? null;
            return r.site.policy ?? null;
          })()}
          onPolicySaved={refetchRecord}
        />
      </Show>

      {/* ── Sous-section : VOLT ── */}
      <Show when={props.activeSection === "obj:volt"}>
        <TenantObjectVault objectType={props.ref.objectType} objectId={props.ref.objectId} />
      </Show>

      {/* ── Sous-section : Hôtes ── */}
      <Show when={props.activeSection === "obj:hosts"}>
        <section class="tenant-section">
          <Show
            when={props.hosts.length > 0}
            fallback={
              <div class="tenant-empty">
                Aucun hôte rattaché à ce {props.ref.objectType === "client" ? "client" : "site"}.
              </div>
            }
          >
            <table class="rmm-table tenant-hosts-table">
              <thead>
                <tr>
                  <th>Hôte</th>
                  <th>Système</th>
                  <th>État</th>
                  <th>Site</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <For each={props.hosts}>
                  {(h) => (
                    <tr>
                      <td>
                        <strong>{h.displayName || h.hostname}</strong>
                        <Show when={h.hostname && h.displayName && h.hostname !== h.displayName}>
                          <div>
                            <small>{h.hostname}</small>
                          </div>
                        </Show>
                      </td>
                      <td>{h.platform ?? "—"}</td>
                      <td>{h.online ? "en ligne" : "hors ligne"}</td>
                      <td>{h.siteLabel ?? "—"}</td>
                      <td>
                        <button
                          class="btn btn--primary btn--xs"
                          disabled={!h.online || !h.persistentSessionCode}
                          onClick={() => props.onConnectHost(h)}
                        >
                          Connecter
                        </button>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </Show>
        </section>
      </Show>
    </div>
  );
};
